import React, { useEffect, useState } from "react";
import isEmpty from "lodash/isEmpty";

import { getBaseUnits, getUnitOptions } from "utils/fields";
import { getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { provinceCodeMap } from "utils/weather";
import { useStateSelector } from "utils/field";
import {
    getHybridVehicleOptionsTemplate,
    getHybridVehicleSpecs,
    getVehicleYearOptions,
    getVehicleManufacturerOptions,
    getVehicleModelOptions,
} from "utils/transportation";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Accordion from "components/Accordion";
import Actions from "../Actions";

import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

import OpenIcon from "assets/images/icons/JSX/Open";

// Validations
const commuteDistanceValidation = getValidation("commuteDistance");
const motorValidation = getValidation("motor");
const engineSizeValidation = getValidation("engineSize");
const emissionsValidation = getValidation("emissionsRate");

// Helper Functions
function resetValues(change, accessor) {
    change(`${accessor}.vehicleClass`, "");
    change(`${accessor}.motor`, "");
    change(`${accessor}.engineSize`, "");
    change(`${accessor}.cylinders`, "");
    change(`${accessor}.transmission`, "");
    change(`${accessor}.emissions`, "");
    change(`${accessor}.emissionRating`, "");
    change(`${accessor}.smogRating`, "");
    change(`${accessor}.fuelType1`, "");
    change(`${accessor}.range1`, "");
    change(`${accessor}.combinedFuelEconomy1`, "");
    change(`${accessor}.combinedElectricityEconomy1`, "");
    change(`${accessor}.rechargeTime`, "");
    change(`${accessor}.fuelType2`, "");
    change(`${accessor}.range2`, "");
    change(`${accessor}.combinedFuelEconomy2`, "");

    change(`${accessor}.annualEmissions`, 0);
    change(`${accessor}.annualFuelConsumption`, 0);
    change(`${accessor}.annualElectricityConsumption`, 0);
    change(`${accessor}.annualFuelCost`, 0);
}

function modelToolTip(model) {
    switch (true) {
        case model.includes("(Low)"):
            return "Performance is calculated annually based on fuel economy (kWh/100km + L/100km) and vehicle class. A 'Low' rating means the vehicle was in the 25th percentile for performance for the year based on these factors.";
        case model.includes("(Mid)"):
            return "Performance is calculated annually based on fuel economy (kWh/100km + L/100km) and vehicle class. A 'Mid' rating means the vehicle was in the 50th percentile for performance for the year based on these factors.";
        case model.includes("(High)"):
            return "Performance is calculated annually based on fuel economy (kWh/100km + L/100km) and vehicle class. A 'High' rating means the vehicle was in the 75th percentile for performance for the year based on these factors.";
        default:
            return "Performance is calculated annually based on fuel economy (kWh/100km + L/100km) and vehicle class.";
    }
}

export default ({ accessor, regularGasPrice, premiumGasPrice, electricityPrice, change, formName }) => {
    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
    const contentOpen = useStateSelector(`model.contentOpen`) || false;

    // Unit Assignment
    const commuteDistanceUnits = useStateSelector(`${accessor}.commuteDistance_u`);
    const distanceUnits = useStateSelector(`${accessor}.distance_u`);
    const motorUnits = useStateSelector(`${accessor}.motor_u`);
    const fuelEconomyUnits = useStateSelector(`${accessor}.fuelEconomy_u`);
    const engineSizeUnits = useStateSelector(`${accessor}.engineSize_u`);
    const emissionsRateUnits = useStateSelector(`${accessor}.emissionsRate_u`);
    const emissionsUnits = useStateSelector(`${accessor}.emissions_u`);
    const electricFuelEconomyUnits = useStateSelector(`${accessor}.electricFuelEconomy_u`);
    const electricityConsumptionUnits = useStateSelector(`${accessor}.electricityConsumption_u`);
    const fuelConsumptionUnits = useStateSelector(`${accessor}.annualFuelConsumption_u`);
    const priceUnits = useStateSelector(`${accessor}.price_u`);

    // Form Value Assignment
    const commuteDistance = useStateSelector(`${accessor}.commuteDistance`) || 0;
    const sysNum = useStateSelector(`${accessor}.systemNumber`) || 1;
    const vehicleYear = useStateSelector(`${accessor}.vehicleYear`) || "";
    const manufacturer = useStateSelector(`${accessor}.manufacturer`) || "";
    const model = useStateSelector(`${accessor}.model`) || "";
    const vehicleClass = useStateSelector(`${accessor}.vehicleClass`) || "";
    const motor = useStateSelector(`${accessor}.motor`) || "";
    const engineSize = useStateSelector(`${accessor}.engineSize`) || "";
    const cylinders = useStateSelector(`${accessor}.cylinders`) || "";
    const transmission = useStateSelector(`${accessor}.transmission`) || "";
    const emissions = useStateSelector(`${accessor}.emissions`) || "";
    const emissionRating = useStateSelector(`${accessor}.emissionRating`) || "";
    const smogRating = useStateSelector(`${accessor}.smogRating`) || "";

    const fuelType1 = useStateSelector(`${accessor}.fuelType1`) || "";
    const range1 = useStateSelector(`${accessor}.range1`) || "";
    const combinedFuelEconomy1 = useStateSelector(`${accessor}.combinedFuelEconomy1`) || "";
    const combinedElectricityEconomy1 = useStateSelector(`${accessor}.combinedElectricityEconomy1`) || "";
    const rechargeTime = useStateSelector(`${accessor}.rechargeTime`) || "";
    const gasUsedInTesting = useStateSelector(`${accessor}.gasUsedInTesting`) || "";

    const fuelType2 = useStateSelector(`${accessor}.fuelType2`) || "";
    const range2 = useStateSelector(`${accessor}.range2`) || "";
    const combinedFuelEconomy2 = useStateSelector(`${accessor}.combinedFuelEconomy2`) || 0;

    // Form Result Assignment
    // const annualDistanceDriven = useStateSelector(`${accessor}.annualDistanceDriven`) || 0;
    // const annualEmissions = useStateSelector(`${accessor}.annualEmissions`) || 0;
    // const annualFuelConsumption = useStateSelector(`${accessor}.annualFuelConsumption`) || 0;
    // const annualFuelCost = useStateSelector(`${accessor}.annualFuelCost`) || 0;

    const { id: locationId } = useStateSelector("model.modelData.location.weather.region") || {};

    // Option Retrieval
    const vehicleMap = getHybridVehicleOptionsTemplate(); //get all electric-vehicle data options
    const yearOpts = getVehicleYearOptions(vehicleMap);
    const manufacturerOpts = getVehicleManufacturerOptions(vehicleYear, vehicleMap);
    const modelOpts = getVehicleModelOptions(vehicleYear, manufacturer, vehicleMap);

    const {
        fuelType1Info,
        fuelType2Info,
        vehicleClassInfo,
        transmissionInfo,
        annualDistanceDriven,
        annualEmissions,
        annualFuelConsumption,
        annualElectricityConsumption,
        annualFuelCost,
    } = getHybridVehicleSpecs(
        commuteDistance,
        {
            //vehicleSpecs
            vehicleYear,
            manufacturer,
            model,
            vehicleClass,
            motor,
            engineSize,
            cylinders,
            transmission,
            emissions,
            emissionRating,
            smogRating,
            fuelType1,
            range1,
            combinedFuelEconomy1,
            combinedElectricityEconomy1,
            rechargeTime,
            fuelType2,
            range2,
            combinedFuelEconomy2,
            region: provinceCodeMap[locationId],
        },
        {
            //fuelPrices
            regularGasPrice,
            premiumGasPrice,
            electricityPrice,
        }
    );

    const [reRenderKey, setReRenderKey] = useState(0);

    useEffect(() => {
        setReRenderKey((prev) => prev + 1); // force re-render if year, model, or manufacturer change
    }, [vehicleYear, model, manufacturer]);

    // Change Event Handlers - specific to EVs
    const handleYearChange = () => {
        change(`${accessor}.manufacturer`, ""); // Reset manufacturer when year changes
        change(`${accessor}.model`, ""); // Reset model when year changes
        resetValues(change, accessor);
    };

    const handleManufacturerChange = (selectedManufacturer) => {
        change(`${accessor}.manufacturer`, selectedManufacturer);
        change(`${accessor}.model`, ""); // Reset model when manufacturer changes
        resetValues(change, accessor);
    };

    const handleModelChange = (selectedModel) => {
        change(`${accessor}.model`, selectedModel);

        const vehicleData = vehicleMap?.[vehicleYear]?.[manufacturer]?.[selectedModel] || {};

        if (isEmpty(vehicleData)) {
            console.error("Vehicle data is not found"); // Handle the case where vehicleData is empty
        } else {
            change(`${accessor}.vehicleClass`, vehicleData.vehicleClass);
            change(`${accessor}.motor`, vehicleData.generalSpecifications.motor);
            change(`${accessor}.engineSize`, vehicleData.generalSpecifications.engineSize);
            change(`${accessor}.cylinders`, vehicleData.generalSpecifications.cylinders);
            change(`${accessor}.transmission`, vehicleData.generalSpecifications.transmission);
            change(`${accessor}.emissions`, vehicleData.generalSpecifications.emissions);
            change(`${accessor}.emissionRating`, vehicleData.generalSpecifications.emissionRating);
            change(`${accessor}.smogRating`, vehicleData.generalSpecifications.smogRating);
            change(`${accessor}.fuelType1`, vehicleData.electricSpecifications.fuelType);
            change(`${accessor}.range1`, vehicleData.electricSpecifications.range);
            change(
                `${accessor}.combinedFuelEconomy1`,
                vehicleData.electricSpecifications.combinedFuelEconomy === 0
                    ? "0.0"
                    : vehicleData.electricSpecifications.combinedFuelEconomy
            );
            change(
                `${accessor}.combinedElectricityEconomy1`,
                vehicleData.electricSpecifications.combinedElectricityEconomy
            );
            change(`${accessor}.gasUsedInTesting`, vehicleData.electricSpecifications.gasUsedInTesting);
            change(`${accessor}.rechargeTime`, vehicleData.electricSpecifications.rechargeTime);
            change(`${accessor}.fuelType2`, vehicleData.gasSpecifications.fuelType);
            change(`${accessor}.range2`, vehicleData.gasSpecifications.range);
            change(`${accessor}.combinedFuelEconomy2`, vehicleData.gasSpecifications.combinedFuelEconomy);
        }
    };

    const createLabel = () => {
        if (vehicleYear && manufacturer && !model) {
            return `${vehicleYear} ${manufacturer}`;
        } else if (vehicleYear && manufacturer && model) {
            return `${vehicleYear} ${manufacturer} ${model}`;
        } else {
            return `Hybrid | Vehicle ${sysNum}`;
        }
    };

    return (
        <Accordion
            className={sharedClasses.accordion}
            heading={<span className={classes.accordionTitle}>{createLabel()}</span>}
            large
            hoverActions={<Actions accessor={accessor} change={change} formName={formName} />}
            forceOpen={contentOpen}
        >
            <div key={reRenderKey}>
                <h4>Driving Behavior</h4>
                <InputRow gridTemplate="1fr 1fr 1fr">
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.commuteDistance`}
                        label="Round-trip Daily Commute Distance"
                        placeholder={"0.0"}
                        validate={commuteDistanceValidation}
                        decimals={getDecimalPlaces("commuteDistance")}
                        change={change}
                        units={{
                            base: getBaseUnits("commuteDistance", modelUnits),
                            options: getUnitOptions("distance"),
                            selected: commuteDistanceUnits,
                            unitType: "distance",
                            accessor: `${accessor}.commuteDistance_u`,
                        }}
                        setValue={Math.min(Math.max(commuteDistance, 0), 1000)}
                    />
                </InputRow>

                <h4>General Specifications</h4>
                <InputRow gridTemplate="1fr 1fr 1fr">
                    <Field
                        component={Select}
                        search={true}
                        name={`${accessor}.vehicleYear`}
                        options={yearOpts}
                        label="Year"
                        placeholder="Select Year"
                        onChange={handleYearChange}
                        setValue={vehicleYear}
                    />
                    <Field
                        component={Select}
                        search={true}
                        name={`${accessor}.manufacturer`}
                        options={manufacturerOpts}
                        label="Manufacturer"
                        placeholder="Select Manufacturer"
                        info={
                            '"Generic" provides pre-selected vehicle models that fall into the 25th (Low), 50th (Mid), and 75th (High) percentiles for their year, based on fuel economy (kWh/100km + L/100km) and vehicle class.'
                        }
                        onChange={handleManufacturerChange}
                        setValue={manufacturer}
                        disabled={!vehicleYear}
                    />
                    <Field
                        component={Select}
                        search={true}
                        name={`${accessor}.model`}
                        options={modelOpts}
                        label="Model"
                        placeholder="Select Model"
                        info={modelToolTip(model)}
                        onChange={handleModelChange}
                        setValue={model}
                        disabled={!vehicleYear || !manufacturer}
                    />
                </InputRow>

                <h4>Vehicle Specifications</h4>
                <InputRow gridTemplate="1fr 1fr 1fr">
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.motor`}
                        label="Motor"
                        validate={motorValidation}
                        decimals={getDecimalPlaces("motor")}
                        change={change}
                        units={{
                            base: getBaseUnits("motor", modelUnits),
                            options: getUnitOptions("motor"),
                            selected: motorUnits,
                            unitType: "motor",
                            accessor: `${accessor}.motor_u`,
                        }}
                        setValue={motor}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.engineSize`}
                        label="Engine Size"
                        validate={engineSizeValidation}
                        decimals={getDecimalPlaces("engineSize")}
                        change={change}
                        info="Total displacement of all cylinders."
                        units={{
                            base: getBaseUnits("engineSize", modelUnits),
                            options: getUnitOptions("engine"),
                            selected: engineSizeUnits,
                            unitType: "engine",
                            accessor: `${accessor}.engineSize_u`,
                        }}
                        setValue={engineSize}
                        disabled={true}
                    />
                    <Field
                        component={Input}
                        name={`${accessor}.cylinders`}
                        label="Cylinders"
                        setValue={cylinders}
                        disabled={true}
                    />
                    <Field
                        component={Input}
                        name={`${accessor}.transmission`}
                        label="Transmission"
                        info={transmissionInfo}
                        change={change}
                        setValue={transmission}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.emissions`}
                        label="CO₂ Emissions Rate"
                        validate={emissionsValidation}
                        decimals={getDecimalPlaces("emissionsRate")}
                        change={change}
                        units={{
                            base: getBaseUnits("emissionsRate", modelUnits),
                            options: getUnitOptions("emissionsRate"),
                            selected: emissionsRateUnits,
                            unitType: "emissionsRate",
                            accessor: `${accessor}.emissionsRate_u`,
                        }}
                        setValue={emissions === null ? "N/A" : emissions} // no emissions rating then set to N/A
                        disabled={true}
                    />
                    <Field
                        component={Input}
                        name={`${accessor}.emissionRating`}
                        label="CO₂ Rating"
                        info="Rated on a scale from 1 (worst) to 10 (best)"
                        change={change}
                        setValue={emissionRating}
                        disabled={true}
                    />
                    <Field
                        component={Input}
                        name={`${accessor}.smogRating`}
                        label="Smog Rating"
                        info="Rated on a scale from 1 (worst) to 10 (best)"
                        change={change}
                        setValue={smogRating}
                        disabled={true}
                    />
                    <Field
                        component={Input}
                        name={`${accessor}.vehicleClass`}
                        label="Vehicle Class"
                        info={vehicleClassInfo}
                        change={change}
                        setValue={vehicleClass}
                        disabled={true}
                    />
                    <Field
                        component={Input}
                        name={`${accessor}.fuelType2`}
                        label="Fuel Type"
                        change={change}
                        setValue={fuelType2Info}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.combinedFuelEconomy2`}
                        label="Combined Fuel Economy"
                        decimals={getDecimalPlaces("fuelEconomy")}
                        info="Value provided by NRCan reflects 55% city driving and 45% highway driving."
                        change={change}
                        units={{
                            base: getBaseUnits("fuelEconomy", modelUnits),
                            options: getUnitOptions("fuelEconomy"),
                            selected: fuelEconomyUnits,
                            unitType: "fuelEconomy",
                            accessor: `${accessor}.fuelEconomy_u`,
                        }}
                        setValue={combinedFuelEconomy2}
                        disabled={true}
                    />
                </InputRow>

                <h4>Electric Specifications</h4>
                <InputRow gridTemplate="1fr 1fr 1fr">
                    <Field
                        component={Input}
                        name={`${accessor}.fuelType1`}
                        label="Fuel Type"
                        change={change}
                        setValue={fuelType1Info}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.combinedFuelEconomy1`}
                        label="Combined Fuel Economy"
                        decimals={getDecimalPlaces("fuelEconomy")}
                        info="Value provided by NRCan reflects 55% city driving and 45% highway driving."
                        change={change}
                        units={{
                            base: getBaseUnits("fuelEconomy", modelUnits),
                            options: getUnitOptions("fuelEconomy"),
                            selected: fuelEconomyUnits,
                            unitType: "fuelEconomy",
                            accessor: `${accessor}.fuelEconomy_u`,
                        }}
                        setValue={combinedFuelEconomy1}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.combinedElectricityEconomy1`}
                        label="Combined Electricity Economy"
                        decimals={getDecimalPlaces("electricFuelEconomy")}
                        info="Value provided by NRCan reflects 55% city driving and 45% highway and assumes 1 litre of gasoline contains the energy equivalent to 8.9 kWh of electricity."
                        change={change}
                        units={{
                            base: getBaseUnits("electricFuelEconomy", modelUnits),
                            options: getUnitOptions("electricFuelEconomy"),
                            selected: electricFuelEconomyUnits,
                            unitType: "electricFuelEconomy",
                            accessor: `${accessor}.electricFuelEconomy_u`,
                        }}
                        setValue={combinedElectricityEconomy1}
                        disabled={true}
                    />
                </InputRow>

                <div className={classes.info}>
                    <h4>Results Preview</h4>
                    <a
                        style={{ marginBottom: "1rem" }}
                        rel="noopener noreferrer"
                        href="https://natural-resources.canada.ca/energy-efficiency/product-energy-ratings/energuide/energuide-labels-plug-hybrid-electric-vehicles"
                        target="_blank"
                        className={sharedClasses.externalLink}
                    >
                        See EnerGuide labels for PHEVs <OpenIcon />
                    </a>
                </div>
                <div className={classes.infoSubtitle}>
                    <div className={classes.disclaimer}>
                        {/* <img src={WarningOrangeIcon} alt="Disclaimer" /> 
                        <div className={classes.disclaimerTitle}>
                            Disclaimer
                        </div>*/}
                        <div className={classes.disclaimerText}>
                            This simulation uses energy consumption values from Natural Resources Canada. For plug-in
                            hybrid electric vehicles (PHEVs), calculations assume that the vehicle operates in
                            electric-only mode until the battery is fully depleted, and then switches to gasoline-only
                            mode. Actual driving patterns may vary based on charging habits, trip length, and driving
                            conditions.
                        </div>
                    </div>
                </div>
                <InputRow gridTemplate="1fr 1fr 1fr">
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.annualDistanceDriven`}
                        label="Annual Distance Driven"
                        decimals={getDecimalPlaces("distance")}
                        change={change}
                        units={{
                            base: getBaseUnits("distance", modelUnits),
                            options: getUnitOptions("distance"),
                            selected: distanceUnits,
                            unitType: "distance",
                            accessor: `${accessor}.distance_u`,
                        }}
                        placeholder={"0.00"}
                        setValue={annualDistanceDriven}
                        disabled={true}
                        info={"Assuming only daily commute driving, 5 days a week, 50 weeks a year."}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.annualEmissions`}
                        label="Annual Emissions"
                        decimals={getDecimalPlaces("emissions")}
                        change={change}
                        units={{
                            base: getBaseUnits("emissions", modelUnits),
                            options: getUnitOptions("emissions"),
                            selected: emissionsUnits,
                            unitType: "emissions",
                            accessor: `${accessor}.emissions_u`,
                        }}
                        placeholder={"0.00"}
                        setValue={annualEmissions}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.annualFuelConsumption`}
                        label="Annual Fuel Consumption"
                        decimals={getDecimalPlaces("fuelConsumption")}
                        change={change}
                        units={{
                            base: getBaseUnits("fuelConsumption", modelUnits),
                            options: ["L", "gal_imp", "gal_us"],
                            selected: fuelConsumptionUnits,
                            unitType: "volume",
                            accessor: `${accessor}.annualFuelConsumption_u`,
                        }}
                        placeholder={"0.00"}
                        setValue={annualFuelConsumption}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.annualElectricityConsumption`}
                        label="Annual Electricity Consumption"
                        decimals={getDecimalPlaces("electricityConsumption")}
                        change={change}
                        info="Assumes 100% battery use before switching to gas consumption."
                        units={{
                            base: getBaseUnits("electricityConsumption", modelUnits),
                            options: getUnitOptions("electricityConsumption"),
                            selected: electricityConsumptionUnits,
                            unitType: "electricityConsumption",
                            accessor: `${accessor}.electricityConsumption_u`,
                        }}
                        placeholder={"0.00"}
                        setValue={annualElectricityConsumption}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.annualFuelCost`}
                        label="Annual Fuel Cost"
                        decimals={getDecimalPlaces("price")}
                        change={change}
                        units={{
                            base: getBaseUnits("price", modelUnits),
                            options: getUnitOptions("price"),
                            selected: priceUnits,
                            unitType: "price",
                            accessor: `${accessor}.price_u`,
                        }}
                        placeholder={"0.00"}
                        setValue={annualFuelCost}
                        disabled={true}
                    />
                </InputRow>
            </div>
        </Accordion>
    );
};
